import React, { useEffect, useState, useRef } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { Camera, Keyboard } from '@phosphor-icons/react';

let scannerInstanceCounter = 0;

/**
 * Shared camera/manual barcode-or-QR scanner. Generalized from the
 * Html5Qrcode init/cleanup pattern used in QRVerificationPage.js,
 * SecurityDashboardPage.js, and CanteenAdminPage.js — those three usages
 * are left as-is; this component is for new features only.
 *
 * Props:
 *   active: boolean - whether the scanner should be running
 *   onScan(decodedText): called once per successful decode
 *   manualLabel: placeholder text for the manual-entry fallback
 */
function BarcodeScanner({ active, onScan, manualLabel = 'Enter code manually' }) {
  const [scanMode, setScanMode] = useState('camera');
  const [cameraError, setCameraError] = useState(null);
  const [manualValue, setManualValue] = useState('');
  const elementIdRef = useRef(`barcode-scanner-${++scannerInstanceCounter}`);
  const manualRef = useRef(null);

  useEffect(() => {
    if (!active || scanMode !== 'camera') return undefined;

    let isActive = true;
    let started = false;
    const html5Qr = new Html5Qrcode(elementIdRef.current);

    html5Qr.start(
      { facingMode: 'environment' },
      { fps: 10, qrbox: 220 },
      (decodedText) => {
        if (!isActive) return;
        isActive = false;
        if (started) html5Qr.stop().catch(() => {});
        onScan(decodedText);
      },
      () => { /* per-frame scan miss — ignore */ }
    ).then(() => {
      started = true;
    }).catch((err) => {
      if (!isActive) return;
      setScanMode('manual');
      setCameraError('Camera unavailable — enter the code manually.');
      console.warn('Barcode camera start failed:', err);
    });

    return () => {
      isActive = false;
      // html5-qrcode throws synchronously if stop() is called before start()
      // has resolved (e.g. permission denied) — only stop once started.
      if (started) {
        html5Qr.stop().then(() => html5Qr.clear()).catch(() => {});
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, scanMode]);

  useEffect(() => {
    if (active && scanMode === 'manual' && manualRef.current) {
      setTimeout(() => manualRef.current?.focus(), 100);
    }
  }, [active, scanMode]);

  if (!active) return null;

  const submitManual = () => {
    const value = manualValue.trim();
    if (!value) return;
    onScan(value);
    setManualValue('');
  };

  return (
    <div>
      {scanMode === 'camera' ? (
        <>
          <div id={elementIdRef.current} style={{ width: '100%', minHeight: 220, borderRadius: 12, overflow: 'hidden', background: '#0f172a', marginBottom: 8 }} />
          <button type="button" className="btn btn-secondary" onClick={() => setScanMode('manual')} style={{ width: '100%' }}>
            <Keyboard size={14} /> {manualLabel}
          </button>
        </>
      ) : (
        <>
          {cameraError && <div style={{ fontSize: 12, color: '#dc2626', marginBottom: 6 }}>{cameraError}</div>}
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              ref={manualRef}
              type="text"
              className="form-control"
              value={manualValue}
              onChange={(e) => setManualValue(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && submitManual()}
              placeholder={manualLabel}
            />
            <button type="button" className="btn btn-primary" onClick={submitManual} disabled={!manualValue.trim()}>Go</button>
          </div>
          <button type="button" className="btn btn-secondary" onClick={() => { setScanMode('camera'); setCameraError(null); }} style={{ width: '100%', marginTop: 8 }}>
            <Camera size={14} /> Use camera instead
          </button>
        </>
      )}
    </div>
  );
}

export default BarcodeScanner;
